import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { Logger } from '../../../infrastructure/logging/logger';
import { AdminPortalAPIHttpClient } from '../../../infrastructure/http/v2/AdminPortalAPIHttpClient';
import { isStringEmptyOrNull } from '../../../utilities/StringUtility';
import { Currency } from '../models/Currency';
import { formatCurrencyString } from '../../../utilities/CurrencyUtility';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';

@Injectable({
  providedIn: 'root'
})
export class CurrenciesService {

  twoDecimalPlaceCurrencyFormat = '1.2-2';
  defaultCurrencyCode = 'USD';

  constructor(
    private adminPortalHttpClient: AdminPortalAPIHttpClient,
    private logService: Logger,
    private snackbar: MatSnackBar,
    private userContextProvider: UserContextProvider
  ) { }

  async getCurrencies(): Promise<Currency[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/currencies`));
      return Promise.resolve(response.data);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Currencies', 'An error occurred while retrieving Currencies');
      return Promise.reject(`Error while retrieving currencies detail`);
    }
  }

  async createCurrency(payload: any): Promise<Currency> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.post(`/currencies`, payload));
      if (!response) {
        return Promise.reject(`Create currency response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err, 'Create');
        }
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating currency: ${e}`);
    }
  }

  async updateCurrency(currencyId: number, currency: any): Promise<any> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/currencies/${currencyId}`, currency));
      if (!response) {
        return Promise.reject(`Update currency response null ${currencyId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err, 'Update');
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating currency ${currencyId}: ${e}`);
    }
  }

  async deleteCurrency(currencyId: number): Promise<Currency> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.delete(`/currencies/delete/${currencyId}`));
      if (!response) {
        return Promise.reject(`Delete currency response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err, 'Delete');
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while deleting verification package: ${e}`);
    }
  }

  prependCurrencySymbol(currency: Currency | null | undefined, price: string): string {
    if (currency === null || currency === undefined) {
      return price;
    }
    
    if(price === "null" || price === null){
      price = "0"
    }

    const formatted = formatCurrencyString(price, this.userContextProvider.currencyCode);
    // const formatted = this.currencyPipe.transform(price, currency.code ?? this.defaultCurrencyCode, 'symbol', this.twoDecimalPlaceCurrencyFormat);
    return (formatted !== null && formatted !== undefined) ? formatted : price;
  }

  displaySnackbar(errorMessage: string, method?: string) {
    if (isStringEmptyOrNull(errorMessage)) {
      this.displaySuccessSnackbar(method);
    } else {
      this.displayErrorSnackbar(errorMessage);
    }
  }
    
  displaySuccessSnackbar(method?: string) {
    this.snackbar.open(`${method} Successful`, 'OK', { duration: 4000 });
  }

  displayErrorSnackbar(errorMessage: string) {
    this.snackbar.open(`Error Saving Policy: ${errorMessage}`, 'OK', { duration: 4000 });
  }
}